/**
 * AI Chat Hooks
 * React Query hooks for AI chat conversations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createConversation,
  createOnDemandReflect,
  deleteConversation,
  getConversation,
  listConversations,
  updateConversation,
} from "../services/ai-chat.service"

export function useAiConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["ai-conversations", page, limit],
    queryFn: () => listConversations(page, limit),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: false, // Prevent duplicate calls from StrictMode
  })
}

export function useAiConversation(id: string | undefined) {
  return useQuery({
    queryKey: ["ai-conversation", id],
    queryFn: () => getConversation(id as string),
    enabled: !!id,
    staleTime: 10 * 1000,
  })
}

export function useCreateAiConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
    },
  })
}

export function useUpdateAiConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: { title?: string; status?: "active" | "archived" }
    }) => updateConversation(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
      // The chat-header reads `conversationData.conversation.status` from
      // this detail query to flip between Archive and Unarchive. Without
      // the explicit invalidation it kept showing the pre-mutation value
      // until staleTime.
      queryClient.invalidateQueries({ queryKey: ["ai-conversation", variables.id] })
    },
  })
}

export function useDeleteAiConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
    },
  })
}

/**
 * Create or get the on-demand reflect conversation for a completed ritual.
 * Used by the Reflect button in My Rituality; idempotent per (user, ritual).
 */
export function useCreateOnDemandReflect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ritualId: string) => createOnDemandReflect(ritualId),
    onSuccess: result => {
      if (result.created) {
        queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
        queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      }
    },
  })
}
